import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
  CTooltip,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getItem, getList } from "../../api/generics";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { PYG } from "../../currency/available-currencies";
import { formatToCurrency } from "../../currency/format";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { INVENTORY_VALUES_USE } from "../../auth/permissions";
import InventoryLocation, {
  InventoryLocationValueLog,
} from "../../models/inventory-location";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import { defaultValueOnUndefined } from "../../utils/fields";
import { formatDateTime } from "../../utils/dates";
import StoreDebtLogDeleteModal from "./StoreDebtDeleteModal";
import StoreDebtLogUpdateModal from "./StoreDebtUpdateModal";
import Store from "../../models/store";

const ITEMS_PER_PAGE = 10;

const StoreDebtList = () => {
  const fields = [
    {
      key: "date",
      _classes: ["text-center"],
      label: "Fecha",
    },
    {
      key: "obs",
      _classes: ["text-center"],
      label: "Observacion",
    },
    {
      key: "previousAmount",
      _classes: ["text-center"],
      label: "Valor anterior",
    },
    {
      key: "amount",
      _classes: ["text-center"],
      label: "Valor Operación",
    },

    {
      key: "afterAmount",
      _classes: ["text-center"],
      label: "Valor Resultante",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const { id } = useParams<{ id: string }>();

  const [search, setSearch] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<InventoryLocationValueLog[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<
    InventoryLocationValueLog | undefined
  >();
  const [currentStore, setCurrentStore] = useState<Store | undefined>();
  const [currentLocation, setCurrentLocation] = useState<
    InventoryLocation | undefined
  >();
  const [startDate, setStartDate] = useState<string | moment.Moment>("");
  const [endDate, setEndDate] = useState<string | moment.Moment>("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeteleModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let startDate = urlParams.get("start_date")
      ? urlParams.get("start_date")
      : "";
    let endDate = urlParams.get("end_date") ? urlParams.get("end_date") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);

    if (startDate !== "") map.set("start_date", startDate);
    if (endDate !== "") map.set("end_date", endDate);
    return map;
  };

  const fetchEntries = async () => {
    const urlParams = getURLParams();
    urlParams.set("store_id", id!.toString());
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const entriesStatus = await getList<InventoryLocationValueLog>(
      "/inventory_locations/value_logs/",
      limit,
      offset,
      urlParams
    );
    if (entriesStatus.status === SUCCESS) {
      if (entriesStatus.data !== undefined) {
        const count = entriesStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setEntries(entriesStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = entriesStatus.detail
        ? entriesStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const fetchStore = async () => {
    const storeStatus = await getItem<Store>(`/stores/${id}/`);
    if (storeStatus.status === SUCCESS) {
      if (storeStatus.data !== undefined) {
        setCurrentStore(storeStatus.data);
      }
    } else {
      const message = storeStatus.detail
        ? storeStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
  };

  const fetchStoreLocation = async () => {
    const storeStatus = await getItem<InventoryLocation>(
      `/inventory_locations/from_store/${id}/`
    );
    if (storeStatus.status === SUCCESS) {
      if (storeStatus.data !== undefined) {
        setCurrentLocation(storeStatus.data);
      }
    } else {
      const message = storeStatus.detail
        ? storeStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    if (previousParams.get("start_date")) {
      urlParams.set("start_date", previousParams.get("start_date"));
    }
    if (previousParams.get("end_date")) {
      urlParams.set("end_date", previousParams.get("end_date"));
    }
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onStartDatehange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      setStartDate("");
      return;
    }
    setStartDate(value);
  };

  const onEndDatehange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      setEndDate("");
      return;
    }
    setEndDate(value);
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    if (search !== "") {
      urlParams.set("search", search);
    } else {
      urlParams.delete("search");
    }
    if (moment.isMoment(startDate)) {
      urlParams.set("start_date", startDate.toISOString());
    } else if (startDate !== undefined && startDate !== "") {
      urlParams.set("start_date", startDate);
    } else {
      urlParams.delete("start_date");
    }
    if (moment.isMoment(endDate)) {
      urlParams.set("end_date", endDate.toISOString());
    } else if (endDate !== undefined && endDate !== "") {
      urlParams.set("end_date", endDate);
    } else {
      urlParams.delete("end_date");
    }
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (log: InventoryLocationValueLog) => {
    setSelectedEntry(log);
    setShowEditModal(true);
  };

  const onDeleteClick = (log: InventoryLocationValueLog) => {
    setSelectedEntry(log);
    setShowDeleteModal(true);
  };

  const onEditCancel = () => {
    setSelectedEntry(undefined);
    setShowEditModal(false);
  };

  const onEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setEntries([]);
    setLoading(true);
    fetchEntries();
  };

  const onDeleteCancel = () => {
    setSelectedEntry(undefined);
    setShowDeleteModal(false);
  };

  const onDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setEntries([]);
    setLoading(true);
    fetchEntries();
  };

  const getInitialValues = async () => {
    setLoading(true);
    const storePromise = fetchStore();
    const entriesPromise = fetchEntries();
    const locationPromise = fetchStoreLocation();

    await storePromise;
    await entriesPromise;
    await locationPromise;

    setLoading(false);
  };

  useEffect(() => {
    getInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      fetchEntries();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Deuda de tienda: {currentStore?.name}</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent
                  permissionName={INVENTORY_VALUES_USE}
                >
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    &nbsp; Agregar Pago Recibido
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <h5>Filtros</h5>
                </CCol>
              </CRow>
              <CRow>
                <CCol></CCol>
              </CRow>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Desde:</CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={onStartDatehange}
                    value={startDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Hasta:</CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={onEndDatehange}
                    value={endDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={entries}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    amount: (item: InventoryLocationValueLog) => {
                      return (
                        <td className="text-center">
                          <b>
                            {item.amount
                              ? formatToCurrency(item.amount, PYG)
                              : "-"}
                          </b>
                        </td>
                      );
                    },
                    previousAmount: (item: InventoryLocationValueLog) => {
                      return (
                        <td className="text-center">
                          {item.previousValue
                            ? formatToCurrency(item.previousValue, PYG)
                            : "-"}
                        </td>
                      );
                    },
                    afterAmount: (item: InventoryLocationValueLog) => {
                      const afterAmount: number =
                        (item.previousValue ?? 0) + (item.amount ?? 0);

                      return (
                        <td className="text-center">
                          <b>{formatToCurrency(afterAmount, PYG)}</b>
                        </td>
                      );
                    },
                    obs: (item: InventoryLocationValueLog) => {
                      return (
                        <td className="text-center">
                          {defaultValueOnUndefined("-", item.obs)}
                        </td>
                      );
                    },
                    date: (item: InventoryLocationValueLog) => {
                      return (
                        <td className="text-center">
                          {item.date
                            ? formatDateTime(new Date(item.date))
                            : "-"}
                        </td>
                      );
                    },
                    actions: (item: InventoryLocationValueLog) => {
                      const editButton = (
                        <CTooltip content={"Editar"}>
                          <CButton
                            className="text-white"
                            size="warning"
                            onClick={() => {
                              onUpdateClick(item);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </CButton>
                        </CTooltip>
                      );
                      const deleteButton = (
                        <CTooltip content={"Borrar"}>
                          <CButton
                            className="text-white"
                            size="danger"
                            onClick={() => {
                              onDeleteClick(item);
                            }}
                          >
                            <i className="fa fa-trash"></i>
                          </CButton>
                        </CTooltip>
                      );

                      if (!item.manualEntry) {
                        return (
                          <td className="text-right">
                            Generado automaticamente
                          </td>
                        );
                      }

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            {editButton}
                            {deleteButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <StoreDebtLogDeleteModal
        show={showDeteleModal}
        onCancel={onDeleteCancel}
        onSuccess={onDeleteSuccess}
        storeDebtLog={selectedEntry}
      />
      <StoreDebtLogUpdateModal
        show={showEditModal}
        initialStore={currentStore}
        initialLocation={currentLocation}
        storeDebtLog={selectedEntry}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
      />
    </>
  );
};

export default StoreDebtList;
